import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthStore } from '../../store/auth.store';
import { CompanyThemeService } from '../company-theme.service';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private apiUrl = environment.apiUrl;
  private baseApiUrl = this.apiUrl.endsWith('/') ? this.apiUrl : `${this.apiUrl}/`;
  private authStore = inject(AuthStore);
  private companyThemeService = inject(CompanyThemeService);

  /**
   * Devuelve el usuario actual con logs de depuración sobre el nivel de rol.
   */
  getUserWithDebug(): any {
    const user = localStorage.getItem(this.userKey);
    if (!user) {
      console.log('[auth] No hay usuario en localStorage');
      return null;
    }
    try {
      const parsed = JSON.parse(user);
      if (parsed?.role?.level != null) {
        console.log('[auth] Nivel de rol:', parsed.role.level);
      } else if (Array.isArray(parsed?.roles) && parsed.roles.length) {
        console.log('[auth] Niveles de roles:', parsed.roles.map((r: any) => r?.level));
      } else {
        console.log('[auth] Usuario sin nivel de rol definido');
      }
      return parsed;
    } catch (e) {
      console.log('[auth] Error al parsear usuario:', e);
      return null;
    }
  }
  private readonly tokenKey = 'access_token';
  private readonly refreshTokenKey = 'refresh_token';
  private readonly companyKey = 'company';
  private readonly userKey = 'user';
  private readonly originalTokenKey = 'impersonation_original_access_token';
  private readonly originalRefreshTokenKey = 'impersonation_original_refresh_token';
  private readonly originalUserKey = 'impersonation_original_user';
  private readonly originalCompanyKey = 'impersonation_original_company';

  constructor(
    private router: Router,
    private http: HttpClient
  ) { }

  // === Helpers de storage ===
  private setToken(token: string) {
    localStorage.setItem(this.tokenKey, token);
  }
  private setRefreshToken(refreshToken: string) {
    localStorage.setItem(this.refreshTokenKey, refreshToken);
  }
  private setCompanyId(companyId: number | string) {
    localStorage.setItem(this.companyKey, String(companyId));
  }
  private setUser(user: any) {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  // === Lecturas ===
  getUser(): any {
    const user = localStorage.getItem(this.userKey);
    if (!user) {
      console.log('[auth] No hay usuario en localStorage');
      return null;
    }
    try {
      const parsed = JSON.parse(user);
      if (parsed?.role?.level != null) {
        console.log('[auth] Nivel de rol:', parsed.role.level);
      } else if (Array.isArray(parsed?.roles) && parsed.roles.length) {
        console.log('[auth] Niveles de roles:', parsed.roles.map((r: any) => r?.level));
      } else {
        console.log('[auth] Usuario sin nivel de rol definido');
      }
      return parsed;
    } catch (e) {
      console.log('[auth] Error al parsear usuario:', e);
      return null;
    }
  }
  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }
  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }
  getUserPayload(): any {
    const token = this.getToken();
    if (!token) return null;
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  }

  isImpersonating(): boolean {
    const payload = this.getUserPayload();
    return payload?.impersonation === true || !!localStorage.getItem(this.originalTokenKey);
  }

  isSuperAdmin(): boolean {
    const user = this.getUser();
    const role = user?.role?.role ?? user?.roles?.[0]?.role ?? '';
    return role === 'ROLE_ADMIN' || role === 'ADMIN';
  }

  getImpersonationPayload(): any {
    const payload = this.getUserPayload();
    return payload?.impersonation === true ? payload : null;
  }

  // === Login / Logout ===
  isCorrect(res: any) {
    return (res?.accessToken && res?.user && res?.user?.companyId);
  }

  loginSuccess(res: any) {
    this.setToken(res.accessToken);
    if (res.refreshToken) {
      this.setRefreshToken(res.refreshToken);
    }
    this.setCompanyId(res.user.companyId);
    this.setUser(res.user);
    this.router.navigate(['/']);
  }

  startCompanyImpersonation(companyId: number): Observable<any> {
    const token = this.getToken();
    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;

    return this.http.post<any>(`${this.baseApiUrl}auth/impersonate/company/${companyId}`, {}, { headers }).pipe(
      tap((res) => {
        if (!localStorage.getItem(this.originalTokenKey) && token) {
          localStorage.setItem(this.originalTokenKey, token);
        }
        const refreshToken = this.getRefreshToken();
        if (!localStorage.getItem(this.originalRefreshTokenKey) && refreshToken) {
          localStorage.setItem(this.originalRefreshTokenKey, refreshToken);
        }
        const currentUser = this.getUser();
        if (!localStorage.getItem(this.originalUserKey) && currentUser) {
          localStorage.setItem(this.originalUserKey, JSON.stringify(currentUser));
        }
        const currentCompany = localStorage.getItem(this.companyKey);
        if (!localStorage.getItem(this.originalCompanyKey) && currentCompany) {
          localStorage.setItem(this.originalCompanyKey, currentCompany);
        }

        this.setToken(res.accessToken);
        if (res.refreshToken) {
          this.setRefreshToken(res.refreshToken);
        }
        if (res.user) {
          this.setUser(res.user);
          this.authStore.setSession(res.accessToken || null, res.user);
          if (res.user.companyId != null) {
            this.setCompanyId(res.user.companyId);
          }
        }

        this.router.navigate(['/analytics']);
      })
    );
  }

  startUserImpersonation(userId: number): Observable<any> {
    const token = this.getToken();
    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;

    return this.http.post<any>(`${this.baseApiUrl}auth/impersonate/user/${userId}`, {}, { headers }).pipe(
      tap((res) => {
        if (!localStorage.getItem(this.originalTokenKey) && token) {
          localStorage.setItem(this.originalTokenKey, token);
        }
        const refreshToken = this.getRefreshToken();
        if (!localStorage.getItem(this.originalRefreshTokenKey) && refreshToken) {
          localStorage.setItem(this.originalRefreshTokenKey, refreshToken);
        }
        const currentUser = this.getUser();
        if (!localStorage.getItem(this.originalUserKey) && currentUser) {
          localStorage.setItem(this.originalUserKey, JSON.stringify(currentUser));
        }
        const currentCompany = localStorage.getItem(this.companyKey);
        if (!localStorage.getItem(this.originalCompanyKey) && currentCompany) {
          localStorage.setItem(this.originalCompanyKey, currentCompany);
        }

        this.setToken(res.accessToken);
        if (res.refreshToken) {
          this.setRefreshToken(res.refreshToken);
        }
        if (res.user) {
          this.setUser(res.user);
          this.authStore.setSession(res.accessToken || null, res.user);
          if (res.user.companyId != null) {
            this.setCompanyId(res.user.companyId);
          }
        }

        this.router.navigate(['/analytics']);
      })
    );
  }

  stopImpersonation() {
    const originalToken = localStorage.getItem(this.originalTokenKey);
    const originalRefreshToken = localStorage.getItem(this.originalRefreshTokenKey);
    const originalUser = localStorage.getItem(this.originalUserKey);
    const originalCompany = localStorage.getItem(this.originalCompanyKey);

    if (!originalToken || !originalUser) {
      this.logout();
      return;
    }

    const currentToken = this.getToken();
    const headers = currentToken
      ? new HttpHeaders({ Authorization: `Bearer ${currentToken}` })
      : undefined;

    const restoreOriginalSession = () => {
      this.setToken(originalToken);
      if (originalRefreshToken) {
        this.setRefreshToken(originalRefreshToken);
      } else {
        localStorage.removeItem(this.refreshTokenKey);
      }
      localStorage.setItem(this.userKey, originalUser);
      if (originalCompany) {
        localStorage.setItem(this.companyKey, originalCompany);
      } else {
        localStorage.removeItem(this.companyKey);
      }
      try {
        this.authStore.setSession(originalToken, JSON.parse(originalUser));
      } catch {
        this.authStore.setSession(originalToken, null);
      }

      localStorage.removeItem(this.originalTokenKey);
      localStorage.removeItem(this.originalRefreshTokenKey);
      localStorage.removeItem(this.originalUserKey);
      localStorage.removeItem(this.originalCompanyKey);

      this.router.navigate(['/analytics']);
    };

    this.http.post<void>(`${this.baseApiUrl}auth/impersonate/stop`, {}, { headers }).subscribe({
      next: () => restoreOriginalSession(),
      error: () => restoreOriginalSession()
    });
  }

  logout() {
    this.authStore.setSession(null, null);
    this.companyThemeService.clearTheme();
    localStorage.removeItem(this.originalTokenKey);
    localStorage.removeItem(this.originalRefreshTokenKey);
    localStorage.removeItem(this.originalUserKey);
    localStorage.removeItem(this.originalCompanyKey);
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.companyKey);
    localStorage.removeItem(this.userKey);
    this.router.navigate(['/login']);
  }

  // === Auth estado ===
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp && payload.exp > now;
    } catch {
      return false;
    }
  }

  // === REFRESH de MI USUARIO (/auth/me) ===
  /**
   * Vuelve a descargar /auth/me y actualiza el localStorage: 'user' y 'company'
   * Devuelve el usuario nuevo.
   * Respuesta esperada: { id, username, email, firstName, lastName, role, companyId }
   */
  refreshMe(): Observable<any> {
    const token = this.getToken();
    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;

    return this.http.get<any>(`${this.baseApiUrl}auth/me`, { headers }).pipe(
      tap((user) => {
        if (user) {
          this.setUser(user);
          if (user.companyId != null) {
            this.setCompanyId(user.companyId);
          }
        }
      })
    );
  }

  // === REFRESH de TOKEN (/auth/refresh) ===
  /**
   * Renueva el JWT usando el token actual.
   * Respuesta esperada: { accessToken, refreshToken, tokenType }
   */
  refreshToken(): Observable<{ accessToken: string; refreshToken: string; tokenType: string }> {
    const token = this.getToken();
    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;

    return this.http.post<{ accessToken: string; refreshToken: string; tokenType: string }>(`${this.baseApiUrl}auth/refresh`, {}, { headers }).pipe(
      tap(res => {
        if (res.accessToken) {
          this.setToken(res.accessToken);
        }
        if (res.refreshToken) {
          this.setRefreshToken(res.refreshToken);
        }
      })
    );
  }

  // === CAMBIAR CONTRASEÑA (/auth/change-password) ===
  /**
   * Cambia la contraseña del usuario autenticado actual.
   * Body: { currentPassword, newPassword }
   * Respuesta: 200 sin contenido si es exitoso, 400 si la contraseña actual es incorrecta
   */
  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    const token = this.getToken();
    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : undefined;

    return this.http.post<void>(`${this.baseApiUrl}auth/change-password`, {
      currentPassword,
      newPassword
    }, { headers });
  }

  // === PASSWORD RESET ===

  /**
   * Solicitar recuperación de contraseña (envía email con token)
   * POST /auth/forgot-password
   * Body: { email }
   * Respuesta: 200 OK (siempre, por seguridad)
   */
  forgotPassword(email: string): Observable<void> {
    return this.http.post<void>(`${this.baseApiUrl}auth/forgot-password`, { email });
  }

  /**
   * Verificar si un token de reset es válido
   * GET /auth/verify-reset-token?token=xxx
   * Response: { valid: boolean, message: string, userEmail?: string }
   */
  verifyResetToken(token: string): Observable<{ valid: boolean; message: string; userEmail?: string }> {
    return this.http.get<{ valid: boolean; message: string; userEmail?: string }>(
      `${this.baseApiUrl}auth/verify-reset-token?token=${encodeURIComponent(token)}`
    );
  }

  /**
   * Restablecer contraseña usando el token
   * POST /auth/reset-password
   * Body: { token, newPassword }
   * Respuesta: 200 OK si es exitoso, 400 si el token es inválido/expirado
   */
  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${this.baseApiUrl}auth/reset-password`, {
      token,
      newPassword
    });
  }
}
